import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MdArrowBack, MdDelete, MdTraffic } from 'react-icons/md';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useLanguage } from '../../context/LanguageContext';

export default function AdminStatusSignalsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const [signals, setSignals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [companyName, setCompanyName] = useState('');

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [compRes, sigRes] = await Promise.all([
                api.get(`/companies/${id}`),
                api.get(`/status-signals/company/${id}`)
            ]);
            setCompanyName(compRes.data.data.name);
            setSignals(sigRes.data.data || []);
        } catch (error) {
            toast.error(t('statusSignals.dataLoadFailed'));
        } finally {
            setLoading(false);
        }
    }, [id, t]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleDelete = async (signalId, name) => {
        if (!window.confirm(t('statusSignals.deleteConfirm', { name }))) return;
        try {
            await api.delete(`/status-signals/${signalId}`);
            toast.success(t('statusSignals.deleted'));
            fetchData();
        } catch (error) {
            toast.error(t('statusSignals.deleteFailed'));
        }
    };

    if (loading) return <div className="loading-page"><div className="spinner" style={{ width: 48, height: 48 }}></div></div>;

    return (
        <div className="page-wrapper">
            <div className="page-header" style={{ marginBottom: 32 }}>
                <div className="page-header-left">
                    <button className="btn btn-secondary btn-icon" onClick={() => navigate(-1)} style={{ marginRight: 16 }}>
                        <MdArrowBack />
                    </button>
                    <div>
                        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <MdTraffic style={{ color: 'var(--primary)' }} />
                            {t('statusSignals.title')}
                        </h1>
                        <p className="page-subtitle" style={{ margin: '4px 0 0 0', color: 'var(--text-secondary)' }}>
                            {t('statusSignals.subtitle', { company: companyName })}
                        </p>
                    </div>
                </div>
            </div>

            <div className="card" style={{ padding: 24 }}>
                {signals.length === 0 ? (
                    <div className="empty-state" style={{ padding: '60px 0' }}>
                        <div className="empty-state-icon" style={{ fontSize: 48 }}>🚥</div>
                        <div className="empty-state-title" style={{ fontSize: 18 }}>{t('statusSignals.noSignals')}</div>
                        <div className="empty-state-desc">{t('statusSignals.noSignalsDesc')}</div>
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                                    <th style={{ padding: '16px', color: 'var(--text-muted)', fontSize: 12, textTransform: 'uppercase' }}>{t('statusSignals.idAuto')}</th>
                                    <th style={{ padding: '16px', color: 'var(--text-muted)', fontSize: 12, textTransform: 'uppercase' }}>{t('statusSignals.signalName')}</th>
                                    <th style={{ padding: '16px', color: 'var(--text-muted)', fontSize: 12, textTransform: 'uppercase' }}>{t('statusSignals.state0Description')}</th>
                                    <th style={{ padding: '16px', color: 'var(--text-muted)', fontSize: 12, textTransform: 'uppercase' }}>{t('statusSignals.state1Description')}</th>
                                    <th style={{ padding: '16px', color: 'var(--text-muted)', fontSize: 12, textTransform: 'uppercase' }}>{t('statusSignals.currentStatus')}</th>
                                    <th style={{ padding: '16px', color: 'var(--text-muted)', fontSize: 12, textTransform: 'uppercase', textAlign: 'right' }}>{t('common.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {signals.map(signal => (
                                    <tr key={signal._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                        <td style={{ padding: '16px', fontSize: 13, fontFamily: 'monospace', color: 'var(--text-secondary)' }}>
                                            {signal._id}
                                        </td>
                                        <td style={{ padding: '16px', fontWeight: 600 }}>{signal.name}</td>
                                        <td style={{ padding: '16px' }}>
                                            <span style={{ color: 'var(--danger)', fontSize: 13 }}>{signal.state0Description}</span>
                                        </td>
                                        <td style={{ padding: '16px' }}>
                                            <span style={{ color: 'var(--success)', fontSize: 13 }}>{signal.state1Description}</span>
                                        </td>
                                        <td style={{ padding: '16px' }}>
                                            {signal.currentValue === null ? (
                                                <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{t('common.noData')}</span>
                                            ) : signal.currentValue === 0 ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--danger)', boxShadow: '0 0 8px var(--danger)' }}></div>
                                                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--danger)' }}>{signal.state0Description}</span>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--success)', boxShadow: '0 0 8px var(--success)' }}></div>
                                                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--success)' }}>{signal.state1Description}</span>
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ padding: '16px', textAlign: 'right' }}>
                                            <button 
                                                className="btn btn-danger btn-sm btn-icon" 
                                                onClick={() => handleDelete(signal._id, signal.name)}
                                                style={{ width: 32, height: 32 }}
                                            >
                                                <MdDelete />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
